/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";
import React, { useEffect, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faAngleDown, faAngleRight } from "@fortawesome/free-solid-svg-icons";
import { faFolderOpen, faFolder, faShareFromSquare } from "@fortawesome/free-regular-svg-icons";
import { useSelector, useDispatch } from "react-redux";
import { RootState, AppDispatch } from "../../redux/store";
import {
  selectedFolder,
  updateContextMenuVisibility,
  addFolder,
  renameFolder,
  deleteFolder,
  fetchFolders,
  fetchFolderReports,
} from "../../redux/folderSlice";
import InputPopUp from "../../components/InputPopUp";
import { InputPopupField } from "../../components/InputPopUp";
import ConfirmPopup from "../../components/ConfirmPopup";
import { showNotification } from '../../redux/notificationSlice';
import { NOOP } from "../../utils/Constants";

const treeStyle = css`
  list-style-type: none;
  padding-left: 10px;
  width: calc(100% - 10px);
  min-width: calc(100% - 10px);
  max-width: 250px;
`;

const parentStyle = css`
  width: calc(100% - 4px);
  min-width: calc(100% - 4px);
  max-width: 250px;
`;

const nodeStyle = (isSelected: boolean): ReturnType<typeof css> => css`
  margin: 5px 0;
  cursor: pointer;
  background-color: ${isSelected ? "#d3d3d3" : "transparent"};
  margin-top: -25px;
  margin-left: 20px;
  padding: 5px;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const toggleStyle = css`
  display: inline-block;
  margin-right: 5px;
`;

const contextMenuStyle = css`
  position: absolute;
  background-color: white;
  border: 1px solid #ccc;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.2);
  z-index: 1000;
  padding: 10px;
  width: 100px;
`;

export interface TreeNode {
  name: string;
  id: number;
  children?: TreeNode[];
}

interface TreeNodeProps {
  node: TreeNode;
  onSelect: (node: TreeNode) => void; // Callback function to handle node selection
  slectedNode: number; // Indicates if the node is selected
  onRightClick: (event: React.MouseEvent, node: TreeNode) => void; // Handle right-click
}

const TreeNode = ({
  node,
  onSelect,
  slectedNode,
  onRightClick,
}: TreeNodeProps) => {
  const [isExpanded, setIsExpanded] = useState(true);
  const dispatch = useDispatch<AppDispatch>();
  const handleToggle = (event: React.MouseEvent) => {
    event.stopPropagation(); // Prevent triggering the select event
    setIsExpanded(!isExpanded);
  };

  const handleSelect = (event: React.MouseEvent) => {
    event.stopPropagation();
    onSelect(node); // Trigger the onSelect callback with the selected node
    dispatch(selectedFolder(node)); // Dispatch the selected folder action
  };

  return (
    <div css={parentStyle}
      onClick={handleSelect}
      onContextMenu={(event) => {
        onRightClick(event, node);
        event.stopPropagation();
      }}
    >
      <span onClick={(node.children ?? []).length > 0 ? handleToggle : NOOP} css={toggleStyle}>
        {(node.children ?? []).length > 0 ? (
          <FontAwesomeIcon icon={isExpanded ? faAngleDown : faAngleRight} />
        ) : (
          node.name === "Shared With Me" ? (
            <FontAwesomeIcon icon={faShareFromSquare} />
          ) : node.name === "Legacy TipNet Queries" ? (
            <FontAwesomeIcon icon={faFolderOpen} />
          ) : (
            <FontAwesomeIcon icon={faFolder} />
          ))}
      </span>

      <div title={node.name} css={nodeStyle(slectedNode === node.id)}>{node.name}</div>

      {isExpanded && node.children && node.children.length > 0 && (
        <div css={treeStyle}>
          {node.children.map((childNode) => (
            <TreeNode
              key={childNode.id}
              node={childNode}
              onSelect={onSelect}
              slectedNode={slectedNode} // Pass the selected node id to child nodes
              onRightClick={onRightClick}
            />
          ))}
        </div>
      )}
    </div>
  );
};

interface TreeProps {
  data: TreeNode[];
}

const Tree = () => {
  const [showPopup, setShowPopup] = useState(false);
  const [showConfirmPopup, setShowConfirmPopup] = useState(false);
  const [confirmPopUp, setConfirmPopUp] = useState({
    title: "",
    message: "",
    confirmAction: (title: string, id: number) => { },
    closeAction: () => { },
    id: 0,
  });
  const [popUpFields, setPopUpFields] = useState<
    { label: string; type: string; id: string; value: string }[]
  >([]);
  const addFolderFields = [
    {
      label: "Name",
      type: "text",
      id: "name",
      value: "",
      maxLength: 50
    },
  ];
  const renameFolderFields = [
    {
      label: "Name",
      type: "text",
      id: "name",
      value: "", // Added the required value property
      maxLength: 50
    },
  ];
  interface PopUpOptions {
    title: string;
    saveAction: (
      selectedNodeId: number,
      title: string,
      fields: InputPopupField[]
    ) => void;
    closeAction: () => void;
  }

  const [popUpOptions, setPopUpOptions] = useState<PopUpOptions>({
    title: "",
    saveAction: (selectedNodeId, title, fields) => {
      if(fields[0].value.trim().toLocaleLowerCase() === 'default') {
          dispatch(showNotification({ message: 'Folder name cannot be "Default"', duration: 3000, isError: true }));
          return;
        }
      if (title === "Add Folder") {
        
        dispatch(addFolder({ name: fields[0].value.trim() })).then((result) => {
          if (result.payload.id === -1) {
            dispatch(showNotification({ message: 'Folder name already exist', duration: 3000, isError: true }));
            return;
          }
          dispatch(fetchFolders());
          setShowPopup(false);
        });
      } else if (title === "Rename Folder") {
        dispatch(renameFolder({ name: fields[0].value.trim(), id: selectedNodeId })).then((result) => {
          if (result.payload === -1) {
            dispatch(showNotification({ message: 'Folder name already exist', duration: 3000, isError: true }));
            return;
          }
          setShowPopup(false);
          dispatch(fetchFolders());
        });
      }

    },
    closeAction: () => {
      setShowPopup(false);
    },
  });
  const dispatch = useDispatch<AppDispatch>();
  const isMenuVisible = useSelector(
    (state: RootState) => state.folder.contextMenuVisible
  );
  const [contextMenu, setContextMenu] = useState<{
    visible: boolean;
    x: number;
    y: number;
    node: TreeNode | null;
  }>({
    visible: false,
    x: 0,
    y: 0,
    node: null,
  });
  const handleRightClick = (event: React.MouseEvent, node: TreeNode) => {
    event.preventDefault(); // Prevent the default browser context menu
    setContextMenu({
      visible: true,
      x: event.clientX - 200,
      y: event.clientY - 100,
      node
    });
    handleSelect(node);
    dispatch(selectedFolder(node));
    dispatch(updateContextMenuVisibility(true));
  };
  const data = useSelector(
    (state: RootState) => state.folder.folders
  ) as TreeNode[];
  const selectedFolderRedux = useSelector((state: RootState) => state.folder.selectedFolder);
  const [selectedNodeId, setSelectedNodeId] = useState<number>(
     selectedFolderRedux ? selectedFolderRedux.id : -1
  ); // Initialize with the first node id or null

  const handleSelect = (node: TreeNode): void => {
    setSelectedNodeId(node.id); // Set the selected node id
  };

  const handleCloseContextMenu = () => {
    setContextMenu({ visible: false, x: 0, y: 0, node: null });
    setPopUpOptions(popUpOptions);
  };

  const handleContextMenuAction = (action: string) => {
    if (action === "Rename") {
      popUpOptions.title = "Rename Folder";
      renameFolderFields[0].value = contextMenu.node?.name ?? "";
      setPopUpFields(renameFolderFields);
      setShowPopup(true);
    } else if (action === "Delete") {
      setShowConfirmPopup(true);
      setConfirmPopUp({
        title: "Delete Folder",
        message: `Are you sure you want to delete ${contextMenu.node?.name}?`,
        confirmAction: (title: string, id: number) => {
          dispatch(deleteFolder({ id })).then(() => {
            dispatch(fetchFolders());
            dispatch(fetchFolderReports());
            dispatch(selectedFolder({ id: -1, name: "My Queries" })); // Reset to default folder
            setSelectedNodeId(-1);
          });
          setShowConfirmPopup(false);
        },
        closeAction: () => {
          setShowConfirmPopup(false);
        },
        id: contextMenu.node?.id ?? 0,
      });
      popUpOptions.title = "Delete Folder";
      setPopUpFields(renameFolderFields);
    } else if (action === "Add Folder") {
      popUpOptions.title = "Add Folder";
      setPopUpFields(addFolderFields);
      setPopUpOptions(popUpOptions);
      setShowPopup(true);
    }

    handleCloseContextMenu();
  };

  useEffect(() => {
    if (!isMenuVisible) {
      setContextMenu({ visible: false, x: 0, y: 0, node: null });
    }
  }, [isMenuVisible]);

  return (
    <div css={treeStyle} onClick={handleCloseContextMenu}>
      {data.map((node) => (
        <TreeNode
          key={node.id}
          node={node}
          onSelect={handleSelect}
          slectedNode={selectedNodeId ?? -1}
          onRightClick={handleRightClick}
        />
      ))}
      {contextMenu.visible && (selectedNodeId === -1 || selectedNodeId > 0) && (
        <div
          css={contextMenuStyle}
          style={{ top: contextMenu.y, left: contextMenu.x }}
          onClick={(e) => e.stopPropagation()} // Prevent closing when clicking inside the menu
        >
          {selectedNodeId > 0 && (
            <div className='context-div' onClick={() => handleContextMenuAction("Rename")}>Rename</div>
          )}
          {selectedNodeId > 0 && (
            <div className='context-div' onClick={() => handleContextMenuAction("Delete")}>Delete</div>
          )}
          {selectedNodeId === -1 && (
            <div className='context-div' onClick={() => handleContextMenuAction("Add Folder")}>
              Add Folder
            </div>
          )}
        </div>
      )}
      {showPopup && (
        <InputPopUp
          saveAction={() =>
            popUpOptions.saveAction(
              selectedNodeId,
              popUpOptions.title,
              popUpFields
            )
          }
          fields={popUpFields}
          title={popUpOptions.title}
          closeAction={popUpOptions.closeAction}
        ></InputPopUp>
      )}
      {showConfirmPopup && (
        <ConfirmPopup
          title={confirmPopUp.title}
          id={confirmPopUp.id}
          message={confirmPopUp.message}
          confirmAction={confirmPopUp.confirmAction}
          closeAction={confirmPopUp.closeAction}
        ></ConfirmPopup>
      )}
    </div>
  );
};

export default Tree;
